import { useLayoutEffect, useRef, useState } from 'react'
import { format } from 'date-fns'
import useReminders, { occurrenceKey } from '../hooks/useReminders'
import { useToast } from '../context/ToastContext'
import { toFirmWallClock } from '../utils/eventTime'
import { minutesLabel, formatRemindAt } from '../utils/reminders'

// App-wide reminder popups. Mounted once in Layout so reminders surface on EVERY
// page (not just Calendar) and stay on screen until the user clicks ✕ — addresses
// feedback #8 ("reminders don't stay up long enough … need a box that stays open
// until it is X'd out").
//
// Data source: singleton useReminders() hook — one 60s poll shared across every
// subscriber in the app (GlobalReminders here + Calendar's inline banner + any
// future consumer).  Before the hook, GlobalReminders and Calendar each ran
// their own setInterval poll against /api/calendar/reminders.
//
// Feedback #828 — a reminder may now fire at a SPECIFIC date/time (remind_at)
// and may be aimed at one person.  The label shows the specific time when
// there is one; a targeted reminder gets a "For you" tag (the server only
// ever serves it to its recipient).  Labels come from utils/reminders.js so
// this card, the Calendar chips and the editor all say the same thing.
//
// Dismiss (✕) and snooze are PERSONAL (2026-09-25): they hide the reminder
// for the person who clicked, never for anyone else who gets it.  To remove a
// reminder for everyone, edit the event and delete it from its Reminders.
//
// ONE CARD PER EVENT (2026-10-12).  An imported Amicus event often carries
// five reminders, so one deadline used to show up to five identical cards and
// people clicked ✕ on each.  The reminders that are DUE for the same event
// now share one card:
// title and time as before, a small "N reminders" note, and each reminder's
// own line ("1 week before", "1 day before" …) so the card shows every
// reminder its buttons act on.  ✕ dismisses, and Snooze snoozes, exactly the
// reminders listed on that card — one PUT per reminder, each echoing its own
// due time (contract C1) — through useReminders' dismissAll, which handles a
// failure per reminder: a 409 drops that reminder, toasts and re-polls (it is
// back at its new time if still due), a 404 re-polls, and any other failure
// (5xx, network) puts that reminder back on the card with an error toast —
// the server recorded nothing, so the card stays for it.  Reminders not yet
// due are never on a card, so never touched.  An event with one due reminder
// renders and behaves exactly as before.
//
// Court deadlines carry legal weight, so a card never acts on a reminder the
// person may not have seen in it.  Two holds, each JOIN_SETTLE_MS long; a
// held reminder stays on the card, the person is told why, and a click after
// the hold covers it:
//   • A reminder that JOINS a card already on screen is held back from that
//     card's ✕ / Snooze (the click may have been aimed at the card before it
//     appeared there).  "Already on screen" is measured from when the CARD
//     first appeared, not from its oldest remaining reminder, and the check
//     runs on every card, one-reminder cards included — otherwise a double
//     click dismissed the older reminders with the first click, left a
//     one-reminder card, and the second click dismissed the newcomer half a
//     second after it arrived (verifier finding, 2026-10-12).
//   • A brand-new GROUPED card ignores ✕ / Snooze altogether: a card that
//     appears above the others pushes them down, and a click aimed at the
//     card that used to be there would otherwise dismiss every reminder on
//     the new one, unseen.  A brand-new one-reminder card is not held, so an
//     event with one reminder behaves exactly as it always has.
const whenLabel = (r) => (r.remind_at ? `Reminder set for ${formatRemindAt(r.remind_at)}` : minutesLabel(r.reminder_minutes))

export const JOIN_SETTLE_MS = 2000

// Snooze presets — user hits one of these instead of only "dismiss".  Dismiss
// = "I saw it, done"; snooze = "remind me again shortly".  Values are
// minutes; the "until tomorrow" preset is dynamic and resolved at click
// time so it always means "9:00 tomorrow morning" regardless of when the
// reminder fires.  Kept short — 4 buttons in a row fit the 320px card
// width without wrapping.
const SNOOZE_PRESETS = [
  { key: '15m',   label: '15m',      minutes: 15,   title: 'Snooze 15 minutes' },
  { key: '1h',    label: '1 hr',     minutes: 60,   title: 'Snooze 1 hour' },
  { key: '3h',    label: '3 hr',     minutes: 180,  title: 'Snooze 3 hours' },
  { key: 'tmrw',  label: 'Tmrw 9am', dynamic: true, title: 'Snooze until 9:00 AM tomorrow' },
]

function nextMorning9am () {
  const t = new Date()
  t.setDate(t.getDate() + 1)
  t.setHours(9, 0, 0, 0)
  return t
}

const dueMs = (r) => {
  const ms = new Date(r.reminder_due_at).getTime()
  return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY
}

/**
 * GET /reminders rows → one group per EVENT, in the order the server listed
 * them (soonest event first).  On a GET /reminders row `id` is the event id
 * (the query selects e.*); a row without one (an API that predates that)
 * gets a group of its own.  Within a group, the reminder that came due first
 * is listed first.
 */
export const groupKeyOf = (r) => (r.id != null ? `event:${r.id}` : `reminder:${r.reminder_id}`)

export function groupRemindersByEvent (list) {
  const groups = []
  const byKey = new Map()
  for (const r of list || []) {
    if (!r) continue
    const key = groupKeyOf(r)
    let g = byKey.get(key)
    if (!g) {
      g = { key, rows: [] }
      byKey.set(key, g)
      groups.push(g)
    }
    g.rows.push(r)
  }
  for (const g of groups) {
    if (g.rows.length > 1) {
      g.rows.sort((a, b) => {
        const x = dueMs(a)
        const y = dueMs(b)
        return x === y ? 0 : (x < y ? -1 : 1)
      })
    }
  }
  return groups
}

// The "For you" and "every Nm" tags for one reminder — shared by a
// single-reminder card and each line of a grouped one.
function ReminderTags ({ r }) {
  return (
    <>
      {r.recipient_user_id != null && (
        <span className="text-[10px] bg-yellow-200 text-yellow-800 px-1.5 rounded"
          title="This reminder was set just for you — other people who can see the event don't get it.">
          👤 For you
        </span>
      )}
      {r.repeat_minutes && (
        <span className="text-[10px] bg-yellow-200 text-yellow-800 px-1.5 rounded" title={`Recurring — re-fires every ${r.repeat_minutes} minutes until the event is marked complete.  Click ✕ to dismiss this fire; the next occurrence still shows.  To stop the recurrence entirely, mark the event complete on Calendar.`}>
          🔁 every {r.repeat_minutes}m
        </span>
      )}
    </>
  )
}

/**
 * The reminders, grouped one card per event, plus the one action every
 * surface that shows those cards uses: dismiss (or, with opts, snooze) every
 * reminder on a card for this person — except any that arrived a moment ago
 * (the join hold below), so nothing is cleared unseen.  Shared by the global
 * cards and the Calendar page's banner so both behave identically.
 */
export function useGroupedReminderActions () {
  const { reminders, dismiss, dismissAll } = useReminders()
  const toast = useToast()

  // occurrence key → when it first appeared on screen, and card (event) key →
  // when that CARD first appeared.  A layout effect, so both are recorded in
  // the same commit that paints them — before the browser can deliver a
  // click.  A card that leaves the screen entirely is forgotten, so if it
  // comes back it counts as new.
  const firstSeenRef = useRef(new Map())
  const cardSeenRef = useRef(new Map())
  useLayoutEffect(() => {
    const now = Date.now()
    const prevRows = firstSeenRef.current
    const prevCards = cardSeenRef.current
    const rows = new Map()
    const cards = new Map()
    for (const r of reminders) {
      const k = occurrenceKey(r.reminder_id, r.reminder_due_at)
      rows.set(k, prevRows.has(k) ? prevRows.get(k) : now)
      const ck = groupKeyOf(r)
      if (!cards.has(ck)) cards.set(ck, prevCards.has(ck) ? prevCards.get(ck) : now)
    }
    firstSeenRef.current = rows
    cardSeenRef.current = cards
  }, [reminders])

  // Split a card's rows into those its button may act on and those held
  // back (see the header).  A row or card with no recorded time counts as
  // having just arrived.
  const settle = (group) => {
    const now = Date.now()
    const rows = group.rows
    const seen = rows.map(r => {
      const t = firstSeenRef.current.get(occurrenceKey(r.reminder_id, r.reminder_due_at))
      return t === undefined ? now : t
    })
    const recorded = cardSeenRef.current.get(group.key)
    const cardSince = Math.min(recorded === undefined ? now : recorded, ...seen)
    if (rows.length > 1 && now - cardSince < JOIN_SETTLE_MS) {
      return { act: [], held: rows.slice(), reason: 'new-card' }
    }
    const act = []
    const held = []
    rows.forEach((r, i) => {
      if (seen[i] > cardSince && now - seen[i] < JOIN_SETTLE_MS) held.push(r)
      else act.push(r)
    })
    return { act, held, reason: 'joined' }
  }

  const tellHeld = ({ held, reason }, isSnooze) => {
    if (held.length === 0) return
    const verb = isSnooze ? 'snooze' : 'dismiss'
    const done = isSnooze ? 'snoozed' : 'dismissed'
    if (reason === 'new-card') {
      toast.info(`This card just appeared, so nothing was ${done}. Click again to ${verb} its ${held.length} reminders.`)
      return
    }
    toast.info(held.length === 1
      ? `A new reminder for this event just came in, so it is still showing. Click again to ${verb} it.`
      : `${held.length} new reminders for this event just came in, so they are still showing. Click again to ${verb} them.`)
  }

  // Both surfaces pass whole rows, not just ids, so the hook can echo the
  // reminder_due_at the user was looking at (code review of 9ff73f1,
  // finding #1; contract C1).  If the event moved meanwhile, the server
  // refuses the click, the hook drops the card and toasts why, and the
  // reminder comes back at its new time instead of being silenced.  A card
  // with one reminder calls dismiss() exactly as before grouping existed
  // (once its reminder is past the join hold, which only ever applies to a
  // reminder that arrived after its card did).  A grouped card always goes
  // through dismissAll, even when the hold leaves one reminder to act on, so
  // a failed PUT puts that reminder back on the card.  `opts` present means
  // snooze ({ minutes } or { snooze_until }).
  const dismissGroup = (group, opts) => {
    const split = settle(group)
    tellHeld(split, !!opts)
    if (split.act.length === 0) return
    if (group.rows.length === 1) {
      if (opts) dismiss(split.act[0], opts)
      else dismiss(split.act[0])
      return
    }
    if (opts) dismissAll(split.act, opts)
    else dismissAll(split.act)
  }

  return { reminders, groups: groupRemindersByEvent(reminders), dismissGroup }
}

export default function GlobalReminders() {
  const { reminders, groups, dismissGroup } = useGroupedReminderActions()
  const [snoozeMenuFor, setSnoozeMenuFor] = useState(null)

  const handleDismiss = (group) => {
    setSnoozeMenuFor(null)
    dismissGroup(group)
  }

  const handleSnooze = (group, preset) => {
    setSnoozeMenuFor(null)
    const opts = preset.dynamic
      ? { snooze_until: nextMorning9am().toISOString() }
      : { minutes: preset.minutes }
    dismissGroup(group, opts)
  }

  if (reminders.length === 0) return null

  return (
    <div className="fixed top-4 right-4 z-[60] w-80 max-w-[calc(100vw-2rem)] space-y-2"
      role="region" aria-label="Reminders">
      {groups.map(group => {
        const rows = group.rows
        const r = rows[0]
        const count = rows.length
        const multi = count > 1
        const anyRepeat = rows.some(x => x.repeat_minutes)
        const showSnooze = snoozeMenuFor === group.key
        const dismissTitle = multi
          ? (anyRepeat
              ? `Dismiss all ${count} reminders for this event — just for you.  Repeating ones come back after their interval.`
              : `Dismiss all ${count} reminders for this event — just for you.  Everyone else who gets them still sees them.`)
          : (r.repeat_minutes
              ? 'Dismiss this fire — just for you.  It comes back after the repeat interval.'
              : 'Dismiss — just for you.  Everyone else who gets this reminder still sees it.')
        return (
          <div key={group.key}
            role="alert"
            aria-live="polite"
            className="bg-yellow-50 border border-yellow-300 rounded-lg shadow-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-2 min-w-0">
                <span className="text-xl leading-none">🔔</span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-yellow-900 break-words">{r.title}</p>
                  <p className="text-xs text-yellow-700 mt-0.5">
                    {/* Firm clock, not the browser's — see utils/eventTime.js. */}
                    {r.start_time && format(toFirmWallClock(r.start_time) || new Date(r.start_time), 'MMM d, yyyy h:mm a')}
                    {r.matter_title && ` · ${r.matter_title}`}
                  </p>
                  {multi ? (
                    <>
                      <p className="text-[11px] font-semibold text-yellow-800 mt-0.5">{count} reminders</p>
                      <ul className="mt-0.5 space-y-0.5">
                        {rows.map(x => (
                          <li key={x.reminder_id} className="text-xs text-yellow-600 flex items-center gap-1 flex-wrap">
                            <span>{whenLabel(x)}</span>
                            <ReminderTags r={x} />
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-xs text-yellow-600 mt-0.5 flex items-center gap-1">
                      <span>{whenLabel(r)}</span>
                      <ReminderTags r={r} />
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => setSnoozeMenuFor(showSnooze ? null : group.key)}
                  title={multi
                    ? `Snooze all ${count} reminders for this event — pick when to remind you again.  Only for you; nobody else's reminder is snoozed.`
                    : 'Snooze — pick when to remind you again.  Only for you; nobody else\'s reminder is snoozed.'}
                  aria-expanded={showSnooze}
                  className="text-yellow-700 hover:text-yellow-900 text-xs font-medium px-2 py-0.5 rounded hover:bg-yellow-100">
                  💤 Snooze
                </button>
                <button onClick={() => handleDismiss(group)}
                  title={dismissTitle}
                  aria-label={multi ? `Dismiss ${count} reminders for ${r.title}` : `Dismiss reminder for ${r.title}`}
                  className="text-yellow-500 hover:text-yellow-800 text-lg leading-none font-bold px-1">✕</button>
              </div>
            </div>
            {showSnooze && (
              <div className="mt-2 pt-2 border-t border-yellow-200 flex items-center gap-1 flex-wrap">
                <span className="text-[10px] uppercase tracking-wider text-yellow-700 font-semibold pr-1">Remind again in</span>
                {SNOOZE_PRESETS.map(p => (
                  <button key={p.key}
                    onClick={() => handleSnooze(group, p)}
                    title={p.title}
                    className="text-xs bg-yellow-200 hover:bg-yellow-300 text-yellow-900 rounded px-2 py-0.5">
                    {p.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
